import { FeatureFlagKey } from "@swivl-mono/types";
import { Input, Spinner } from "@swivl-mono/ui";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { isFeatureEnabled } from "../../../../Helpers/FeatureFlags/isFeatureEnabled";
import FMSModel from "../../../../Models/FMS/FMS.Model";
import { NavBar } from "../../../../Models/NavBar/NavBar.Model";
import { FMSAPIColumn } from "./Columns/FMSAPIColumn";
import { FMSJitSettingsSection } from "./Columns/FMSJitSettings.Section";
import { FMSSettingsColumn } from "./Columns/FMSSettingsColumn";
import "./FMSDetailsView.scss";

export default function FMSDetailsView() {
  const { isLoading, FMSs, isSavingFMS } = FMSModel.useState((s) => s);
  const id = useParams().id;
  const fms = FMSs?.find((fms) => fms.id === Number(id));
  const navigate = useNavigate();
  useEffect(() => {
    FMSModel.load(false);
  }, []);

  useEffect(() => {
    if (fms) {
      NavBar.set(
        <NavBar.Component.Back title="BACK TO SETTINGS" to="/settings/fms-integrations" />,
        <NavBar.Component.Title title={fms.type + " Integration"} />,
        undefined,
        fms.type + " Integration"
      );
    }
  }, [fms, isSavingFMS, isLoading, FMSs]);

  if (!fms) {
    return (
      <div className="FMSDetailsView">
        <Spinner />
      </div>
    );
  }

  const onNameChange = (e) => {
    const value = e.target.value;

    fms.name = value;

    FMSModel.updateFMS(fms, false);
  };

  const onDataChange = (e: any) => {
    const name = e.target.name;
    const value = e.target.value;
    const data = fms.data || {};

    data[name] = value;
    fms.data = data;

    FMSModel.updateFMS(fms, false);
  };

  const handleRemove = (): void => {
    if (window.confirm("Are you sure you want to remove this FMS integration?")) {
      navigate("/settings/fms-integrations");
      FMSModel.deleteFMS(fms);
    }
  };

  const handleNameBlur = () => {
    FMSModel.updateFMS(fms, true, { name: fms.name });
  };

  const onSave = (updates?: { token?: any; data?: any }) => {
    const updatedFms = {
      ...fms,
      ...(updates?.token ? { token: updates.token } : {}),
      ...(updates?.data ? { data: updates.data } : {})
    };
    const payload: Partial<typeof fms> = { name: updatedFms.name, data: updatedFms.data };
    if (updates?.token) {
      payload.token = updates.token;
    }
    FMSModel.updateFMS(updatedFms, true, payload);
  };

  const onSwitch = (name, value) => {
    const data = fms.data || {};

    data[name] = value;
    fms.data = data;

    FMSModel.updateFMS(fms, true, { data: fms.data });
  };

  const onSliderChange = (value) => {
    const data = fms.data || {};

    data.smallMaxSize = value[0].value;
    data.mediumMaxSize = value[1].value;
    data.largeMaxSize = value[2].value;

    fms.data = data;
    FMSModel.updateFMS(fms, false);
  };

  return (
    <div className="FMSDetailsView">
      <div className="leftColumn">
        <div className="siteSection">
          <Input
            label="Integration Name"
            helperText="This helps keep track of this integration when using multiple FMS integrations."
            onChange={onNameChange}
            name="name"
            value={fms.name}
            onBlur={handleNameBlur}
          />
        </div>

        <FMSSettingsColumn fms={fms} onBlur={() => onSave()} onDataChange={onDataChange} onSliderChange={onSliderChange} onSwitch={onSwitch} />
      </div>
      <div className="rightColumn">
        <FMSAPIColumn fms={fms} onSave={onSave} onRemove={handleRemove} />
        {/* JIT freshness drives v3 sequences — Snowy only. It arrived here from the PM v3
            Sequences tab (ENG-3213) without its gate; legacy orgs saw it (ENG-3475). */}
        {isFeatureEnabled(FeatureFlagKey.Snowy) && <FMSJitSettingsSection fmsId={fms.id} fmsType={fms.type} />}
      </div>
    </div>
  );
}
